/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { ActionCreators, Selectors, Status } from 'store/ducks/startUp';
import { StartUpRoutePath, PrivateRoutePath, PRIVATE_DEFAULT_ROUTE } from 'routes/paths';
import { addInitializationErrorEvent } from 'store/ducks/analytics/systemEvent';
import { Modal } from 'ui/components';
import translate from 'services/i18n/translate';
import * as RouterDuck from 'store/ducks/router';

const StartUp: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ from: string } | undefined>();

  const status = useSelector(Selectors.status);
  const error = useSelector(Selectors.error);
  const fromLocation = useSelector(Selectors.fromLocation);
  const [showError, setShowError] = useState(false);
  const isFromProfileList = useSelector(RouterDuck.Selectors.from) === PrivateRoutePath.PROFILES_LIST;

  // start the flow if it is not started yet
  useEffect(() => {
    if (!status) {
      // no status means the flow has not been started
      const from = location.state?.from;
      dispatch(ActionCreators.init(from));
    }
  }, [dispatch, status, location]);

  // handling errors
  useEffect(() => {
    if (error) {
      setShowError(true);
      addInitializationErrorEvent(error);
    }
  }, [error]);

  // handling the status updates and updates the UI in agreement
  useEffect(() => {
    switch (status) {
      case Status.WAITING_UI_AUTHENTICATION:
        history.replace(StartUpRoutePath.SIGNIN_SINGNUP);
        break;
      case Status.WAITING_UI_GDPR_FLOW:
        history.replace(StartUpRoutePath.GDPR);
        return;
      case Status.WAITING_UI_USER_SWITCHING:
        history.replace(PrivateRoutePath.PROFILES_LIST);
        break;
      case Status.SUCCESS: {
        const newLocation = fromLocation && !isFromProfileList ? fromLocation : undefined;
        history.replace(newLocation || PRIVATE_DEFAULT_ROUTE);
        break;
      }
      default:
        break;
    }
  }, [fromLocation, history, isFromProfileList, status]);

  function retry() {
    setShowError(false);
    window.location.reload();
  }

  return error ? (
    <Modal
      title={translate['modal.title.error']}
      message={error.translation}
      buttons={[{ id: 'retry', text: translate['buttons.retry'], onClick: () => retry() }]}
      isShowing={showError}
      setIsShowing={() => false}
    />
  ) : (
    <></>
  );
};

export default StartUp;
